import { useEffect, useRef, useState } from "react";
import type { SessionState } from "../types";
export interface TakeEntry {
  id: string;
  scriptId: string;
  title: string;
  startedAt: number;
  endedAt: number;
  duration: number;
  startWord: number;
  endWord: number;
  mode: string;
  wpm: number;
  language: string;
  notes: string;
}
export interface TakeJournal {
  takes: TakeEntry[];
  storageError: string | null;
  setNotes: (id: string, notes: string) => void;
  exportJournal: (format: "json" | "csv", entries?: TakeEntry[]) => void;
  restore: (values: unknown, idMap: Map<string, string>, takeIds?: Map<string, string>) => void;
  replace: (takes: TakeEntry[]) => void;
}
const KEY = "cue-studio-takes-v1";
function normalize(values: unknown): TakeEntry[] {
  if (!Array.isArray(values)) return [];
  const bounded = (value: unknown, max: number): value is number =>
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= max;
  const result: TakeEntry[] = [];
  for (const value of values) {
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const t = value as Record<string, unknown>;
    if (
      typeof t.id !== "string" ||
      t.id.length > 200 ||
      typeof t.scriptId !== "string" ||
      t.scriptId.length > 200 ||
      typeof t.title !== "string" ||
      !bounded(t.startedAt, 8.64e15) ||
      !bounded(t.endedAt, 8.64e15) ||
      !bounded(t.duration, 1e12) ||
      !bounded(t.startWord, 1000000) ||
      !Number.isInteger(t.startWord) ||
      !bounded(t.endWord, 1000000) ||
      !Number.isInteger(t.endWord) ||
      typeof t.mode !== "string" ||
      !["manual", "constant", "timed", "voice", "sound"].includes(t.mode) ||
      !bounded(t.wpm, 1000) ||
      typeof t.language !== "string" ||
      t.language.length > 80
    )
      continue;
    result.push({
      id: t.id,
      scriptId: t.scriptId,
      title: t.title.slice(0, 200),
      startedAt: t.startedAt,
      endedAt: t.endedAt,
      duration: t.duration,
      startWord: t.startWord,
      endWord: t.endWord,
      mode: t.mode,
      wpm: t.wpm,
      language: t.language,
      notes: typeof t.notes === "string" ? t.notes.slice(0, 10000) : "",
    });
    if (result.length === 500) break;
  }
  return result;
}
function read(): TakeEntry[] {
  try {
    return normalize(JSON.parse(localStorage.getItem(KEY) || "[]"));
  } catch {
    return [];
  }
}
export function prepareRestoredTakes(items: TakeEntry[], values: unknown, idMap: ReadonlyMap<string, string>, takeIds?: ReadonlyMap<string, string>): TakeEntry[] {
  return [
    ...normalize(values).map(take => ({
      ...take,
      id: takeIds?.get(take.id) || crypto.randomUUID(),
      scriptId: idMap.get(take.scriptId) || take.scriptId,
    })),
    ...items,
  ].slice(0, 500);
}
export function useTakeJournal(
  state: SessionState,
  enabled: boolean,
): TakeJournal {
  const [takes, setTakes] = useState(read);
  const [storageError, setStorageError] = useState<string | null>(null);
  const active = useRef<{
    start: SessionState;
    latest: SessionState;
    at: number;
  } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    if (
      active.current &&
      (!state.playing || active.current.start.script.id !== state.script.id)
    ) {
      const take = active.current;
      active.current = null;
      const end =
        state.script.id === take.start.script.id &&
        state.elapsed >= take.latest.elapsed
          ? state
          : take.latest;
      const entry: TakeEntry = {
        id: crypto.randomUUID(),
        scriptId: take.start.script.id,
        title: take.start.script.title,
        startedAt: take.at,
        endedAt: Date.now(),
        duration: Math.max(0, end.elapsed - take.start.elapsed),
        startWord: take.start.wordIndex,
        endWord: end.wordIndex,
        mode: take.start.settings.mode,
        wpm: take.start.settings.wpm,
        language: take.start.settings.language,
        notes: "",
      };
      setTakes((items) => [entry, ...items].slice(0, 500));
    }
    if (state.playing) {
      if (!active.current)
        active.current = { start: state, latest: state, at: Date.now() };
      else active.current.latest = state;
    }
  }, [state, enabled]);
  useEffect(() => {
    if (enabled) {
      try {
        localStorage.setItem(KEY, JSON.stringify(takes));
        setStorageError(null);
      } catch {
        setStorageError(
          "Take journal could not be saved on this device. Export a workspace backup before closing.",
        );
      }
    }
  }, [takes, enabled]);
  const setNotes = (id: string, notes: string) =>
    setTakes((items) =>
      items.map((t) =>
        t.id === id ? { ...t, notes: notes.slice(0, 10000) } : t,
      ),
    );
  const restore = (values: unknown, idMap: Map<string, string>, takeIds?: Map<string, string>) =>
    setTakes(items => prepareRestoredTakes(items, values, idMap, takeIds));
  const exportJournal = (format: "json" | "csv", entries = takes) => {
    const escape = (value: unknown) => {
      const text = String(value ?? "");
      // Quoting alone does not prevent spreadsheet applications evaluating a cell.
      const literal = /^[\s\u0000-\u001f]*[=+\-@]/u.test(text)
        ? "'" + text
        : text;
      return '"' + literal.replaceAll('"', '""') + '"';
    };
    const fields: (keyof TakeEntry)[] = [
      "title",
      "startedAt",
      "endedAt",
      "duration",
      "startWord",
      "endWord",
      "mode",
      "wpm",
      "language",
      "notes",
    ];
    const text =
      format === "json"
        ? JSON.stringify(
            { format: "cue-studio-takes", version: 1, takes: entries },
            null,
            2,
          )
        : fields.join(",") +
          "\n" +
          entries
            .map((t) => fields.map((f) => escape(t[f])).join(","))
            .join("\n");
    const url = URL.createObjectURL(
      new Blob([text], {
        type: format === "json" ? "application/json" : "text/csv;charset=utf-8",
      }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "cue-studio-takes." + format;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return { takes, storageError, setNotes, exportJournal, restore, replace: setTakes };
}
